import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  allowedTableColumnPropTypes,
  allowedTableItemPropTypes
} from '../utils/allowedPropTypes';
import DataTableCell from './_DataTableCell';

/* ------------------------------------------
     * NOTE:
     * <tbody> tag is produced for each individu-
     * al data item to group the nested data into
     * a single row group. The grouped rows are
     * rendered inside each <tbody> element as
     * many times as many is the sum of the nes-
     * ted objects on the item's deepest nesting
     * levels. The cells rendered based on the
     * objects of shallower nesting levels might
     * span multiple rows to indicate that the
     * cells of the deeper nested data are the
     * children of them.
     * ------------------------------------------ */

const getTableRows = function (tableColumns, tableItemData) {
  const finalTableRows = [];

  // Render as many rows as the row span amount on the shallowest nesting level:
  const arrayLen = tableItemData.itemRowsLen;
  for (let i = 0; i < arrayLen; i += 1) {
    const rowCells = [];

    // Loop through all the excisting table columns:
    tableColumns.forEach(columnData => {
      const classNames = [];
      let isCellSkipped = false;
      const cellData = {
        columnHeadingContent: columnData.columnHeadingContent,
        cellContent: null,
        attributes: {

          // This will be the "headers" HTML attribute in the final cell (<td headers=""></td>):
          headers: columnData.columnId
        }
      };

      // Find the cell in the current column that starts on the current row:
      // TODO: this is broken - fix it!
      // - What do I wanna achieve here?
      // - Why don't I assign the found cell to any variable?
      // - Isn't it a foreach that I want to use?
      // - Don't I reassign any function parameter by accident or on purpose?
      tableItemData.tableItemCells.find(foundCell => {
        const isMatchingColumn = foundCell.columnId === columnData.columnId;

        // If the cell starts in the current column and on the current row:
        if (isMatchingColumn && foundCell.firstRowIndex === i) {
          // Set the row span attribute if it is more than 1:
          if (foundCell.spannedRows > 1) {
            cellData.attributes.rowSpan = foundCell.spannedRows;
          }

          // If the data is nested:
          if (foundCell.isNestedCell) {
            // Add class name:
            classNames.push('table-cell-nested');
          }

          // If it is a grouping cell:
          if (columnData.isGroupColumn) {
            cellData.nestedCells = foundCell.nestedCells;
          }

          // Redefine the table cell to be pushed into the row:
          cellData.cellContent = foundCell.cellContent;

          // Hide definition terms added to the cell content for mobile devices:
          cellData.isMobileTermHidden = columnData.isMobileTermHidden;

          // Return that the cell is found:
          return true;
        }

        // Skip adding the current cell if the previous cell spans the current row:
        if (isMatchingColumn && (
          foundCell.firstRowIndex < i
          && foundCell.firstRowIndex + foundCell.spannedRows > i)
        ) {
          isCellSkipped = true;
        }

        return undefined;
      });

      // If cell is not skipped:
      if (!isCellSkipped) {
        const bIsGroupingCell = columnData.isGroupColumn;

        // Add special class names to grouping cells:
        if (bIsGroupingCell) {
          classNames.push('table-cell-group');
        }

        // Add special class names to empty cells:
        if (!cellData.cellContent) {
          classNames.push('table-cell-empty');
        }

        // Add special class names to aligned columns:
        if (columnData.columnTextAlign) {
          classNames.push(`column-align-${columnData.columnTextAlign}`);
        }

        // Add classnames if defined:
        if (columnData.classNames) {
          classNames.push(...columnData.classNames);
        }

        // Remove the cell padding from the table cells (not from the th elements):
        if (columnData.noCellPadding) {
          classNames.push('column-no-padding');
        }

        // Add class names to the attributes if there is any:
        if (classNames.length > 0) {
          cellData.attributes.className = classNames.join(' ');
          const finalClassName = classNames.join(' ');
          const prevClassNames = cellData.attributes.className;
          cellData.attributes.className = (prevClassNames && prevClassNames !== finalClassName)
            ? `${prevClassNames} ${finalClassName}`
            : finalClassName;
        }

        // Add the table cell to the table row:
        rowCells.push(<DataTableCell key={uuidv4()} cellData={cellData} />);
      }
    });

    // Add the row to the final rows:
    finalTableRows.push(<tr key={uuidv4()}>{rowCells}</tr>);
  }

  // Export the final array of rows to be rendered:
  return finalTableRows;
};

const DataTableItem = ({ tableColumns, tableItemData }) => (
  <tbody>
    {getTableRows(tableColumns, tableItemData)}
  </tbody>
);

DataTableItem.propTypes = {
  tableColumns: PropTypes.arrayOf(PropTypes.shape(allowedTableColumnPropTypes)).isRequired,
  tableItemData: PropTypes.shape(allowedTableItemPropTypes)
};

export default DataTableItem;